// Best-effort converter: legacy Razor (.cshtml) badge views -> flat-template JS strings
// (the self-contained HTML/<script> format consumed by FlatTemplateSetup, using
// %ATTENDEEINFO% + JSON.parse(attendees) instead of server-side @model binding).
//
// This is a heuristic assist tool, not a general Razor/C# transpiler. It converts the
// mechanical, low-risk parts (CSS extraction, @helper/@functions signatures, common
// LINQ/string idioms, @if/@else + inline @expr markup) and leaves a `TODO(convert)`
// marker anywhere it can't confidently translate so a developer can finish by hand.
// Known limitation: bare (non-@) "if (" is always treated as Razor control flow, which can
// misfire if literal badge text legitimately contains that substring.

function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function matchBalanced(text, openIdx, openCh, closeCh) {
    let depth = 0
    for (let i = openIdx; i < text.length; i++) {
        if (text[i] === openCh) depth++
        else if (text[i] === closeCh) {
            depth--
            if (depth === 0) return i
        }
    }
    return text.length - 1
}

function indent(text, spaces) {
    const pad = ' '.repeat(spaces)
    return (text || '').split('\n').map(line => (line ? pad + line : line)).join('\n')
}

function stripRazorComments(source) {
    let out = ''
    let i = 0
    while (i < source.length) {
        if (source.startsWith('@*', i)) {
            const end = source.indexOf('*@', i + 2)
            i = end === -1 ? source.length : end + 2
            continue
        }
        out += source[i]
        i++
    }
    return out
}

function extractStyles(source) {
    const sectionIdx = source.indexOf('@section styles')
    let block = source
    if (sectionIdx !== -1) {
        const braceIdx = source.indexOf('{', sectionIdx)
        const endIdx = matchBalanced(source, braceIdx, '{', '}')
        block = source.slice(braceIdx + 1, endIdx)
    }
    const styleMatch = block.match(/<style[\s\S]*?<\/style>/)
    return styleMatch ? styleMatch[0] : ''
}

// "bool isGuest = false" -> { name: 'isGuest', jsParam: 'isGuest = false' } (JS supports
// default parameter values with the same syntax, so this carries over almost verbatim).
function parseParamDecl(raw) {
    const eqIdx = raw.indexOf('=')
    const declPart = eqIdx === -1 ? raw : raw.slice(0, eqIdx)
    const defaultPart = eqIdx === -1 ? null : raw.slice(eqIdx + 1).trim()
    const name = declPart.trim().split(/\s+/).pop()
    return { name, jsParam: defaultPart ? `${name} = ${defaultPart}` : name }
}

function extractHelpers(source) {
    const helpers = []
    const re = /@helper\s+(\w+)\s*\(([^)]*)\)/g
    let m
    while ((m = re.exec(source))) {
        const name = m[1]
        const params = splitTopLevelArgs(m[2]).map(p => p.trim()).filter(Boolean).map(parseParamDecl)
        const braceIdx = source.indexOf('{', re.lastIndex)
        if (braceIdx === -1) break
        const endIdx = matchBalanced(source, braceIdx, '{', '}')
        const body = source.slice(braceIdx + 1, endIdx)
        helpers.push({ name, params, body })
        re.lastIndex = endIdx + 1
    }
    return helpers
}

function extractForLoop(source) {
    const idx = source.indexOf('@for')
    if (idx === -1) return null
    const headerMatch = /@for\s*\(\s*int\s+(\w+)\s*=/.exec(source.slice(idx, idx + 60))
    const indexVar = headerMatch ? headerMatch[1] : 'i'
    const braceIdx = source.indexOf('{', idx)
    if (braceIdx === -1) return null
    const endIdx = matchBalanced(source, braceIdx, '{', '}')
    let body = source.slice(braceIdx + 1, endIdx)

    const itemDeclRe = new RegExp(`\\b\\w+\\s+(\\w+)\\s*=\\s*@?[\\w.]+\\[${indexVar}\\]\\s*;`)
    const itemMatch = itemDeclRe.exec(body)
    const paramName = itemMatch ? itemMatch[1] : 'attendee'
    if (itemMatch) body = body.slice(0, itemMatch.index) + body.slice(itemMatch.index + itemMatch[0].length)

    return { body, paramName, indexVar }
}

function extractAndStripGuestLoop(body) {
    const foreachMatch = /foreach\s*\(\s*\w+\s+(\w+)\s+in\s+[^)]+\)/.exec(body)
    if (!foreachMatch) return { mainBody: body, guestBody: null, guestParamName: null }
    const foreachStart = foreachMatch.index
    const braceIdx = body.indexOf('{', foreachStart + foreachMatch[0].length)
    const braceEnd = matchBalanced(body, braceIdx, '{', '}')
    const guestParamName = foreachMatch[1]
    const guestBody = body.slice(braceIdx + 1, braceEnd)

    // Strip the nearest enclosing if(...) block that fully wraps the foreach too (typically
    // a "has guests" null/count guard), falling back to stripping just the foreach itself.
    let stripStart = foreachStart
    let stripEnd = braceEnd + 1
    const before = body.slice(0, foreachStart)
    const ifRe = /\bif\s*\(/g
    let m
    const candidates = []
    while ((m = ifRe.exec(before))) candidates.push(m.index)
    for (let k = candidates.length - 1; k >= 0; k--) {
        const ifIdx = candidates[k]
        const parenIdx = body.indexOf('(', ifIdx)
        const parenEnd = matchBalanced(body, parenIdx, '(', ')')
        const blockBraceIdx = body.indexOf('{', parenEnd)
        if (blockBraceIdx === -1 || blockBraceIdx > foreachStart) continue
        const blockBraceEnd = matchBalanced(body, blockBraceIdx, '{', '}')
        if (blockBraceEnd > braceEnd) {
            stripStart = ifIdx
            stripEnd = blockBraceEnd + 1
            break
        }
    }
    const mainBody = body.slice(0, stripStart) + body.slice(stripEnd)
    return { mainBody, guestBody, guestParamName }
}

// Top-level "@{ ... }" blocks before the main loop/helpers often declare page-scope aliases
// (e.g. "var selections = Model.AttendeeSelections;", or repository objects later referenced
// as "udfr.GetUdfs(...)" inside the loop/helpers). Convert them to page-scope JS statements so
// those later references don't throw ReferenceErrors, even though repository-style calls still
// need a human to wire up real data (flagged via the generic "new Type()" stub above).
function extractPagePreambleEntries(source, beforeIdx) {
    const entries = []
    let searchFrom = 0
    for (;;) {
        const idx = source.indexOf('@{', searchFrom)
        if (idx === -1 || idx >= beforeIdx) break
        const braceIdx = idx + 1
        const endIdx = matchBalanced(source, braceIdx, '{', '}')
        const block = source.slice(braceIdx + 1, endIdx)
        for (const line of block.split('\n')) {
            const trimmed = line.trim()
            if (!trimmed || /^(Layout|ViewBag\.)/.test(trimmed)) continue
            const js = convertCSharpExpressionTokens(line, null, null)
            const nameMatch = /^\s*let\s+(\w+)\s*=/.exec(js)
            entries.push({ name: nameMatch ? nameMatch[1] : null, js })
        }
        searchFrom = endIdx + 1
    }
    return entries
}

function extractFunctionsBlock(source) {
    const idx = source.indexOf('@functions')
    if (idx === -1) return []
    const braceIdx = source.indexOf('{', idx)
    const endIdx = matchBalanced(source, braceIdx, '{', '}')
    const block = source.slice(braceIdx + 1, endIdx)
    const methods = []
    const re = /(?:private|public|internal|protected)\s+[\w<>,[\]\s]+?\s+(\w+)\s*\(([^)]*)\)\s*/g
    let m
    while ((m = re.exec(block))) {
        const name = m[1]
        const paramDecl = (m[2].split(',')[0] || '').trim()
        const paramName = paramDecl.split(/\s+/).pop() || 'item'
        const braceIdx2 = block.indexOf('{', re.lastIndex)
        if (braceIdx2 === -1) continue
        const endIdx2 = matchBalanced(block, braceIdx2, '{', '}')
        const body = block.slice(braceIdx2 + 1, endIdx2)
        methods.push({ name, paramName, body })
        re.lastIndex = endIdx2 + 1
    }
    return methods
}

function splitTopLevelArgs(text) {
    const parts = []
    let depth = 0
    let last = 0
    for (let i = 0; i < text.length; i++) {
        const ch = text[i]
        if (ch === '(' || ch === '[' || ch === '{') depth++
        else if (ch === ')' || ch === ']' || ch === '}') depth--
        else if (ch === ',' && depth === 0) {
            parts.push(text.slice(last, i))
            last = i + 1
        }
    }
    parts.push(text.slice(last))
    return parts
}

// Default "regId" source field per model family - which flat-JSON field a template's barcode
// should read, mirroring FlatTemplateSetup's own built-in default template. Overridden per
// family where the real legacy templates were observed using a different field (Impexium's
// LocalClientId, Rhythm's Number, iMIS's PartyId); everything else defaults to
// LocalRegistrationId.
const REGID_FIELD_BY_FAMILY = {
    impexium: 'LocalClientId',
    rhythm: 'Number',
    imis: 'PartyId',
}
let currentRegIdField = 'LocalRegistrationId'

function convertUrlActionBarcode(expr, sourceParamName, targetName) {
    const m = /Url\.Action\(\s*"(Render\w*(?:Barcode|QrCode|Code))"\s*,\s*"Print"\s*,\s*new\s*\{([\s\S]*?)\}\s*\)/.exec(expr)
    if (!m) return null
    const action = m[1]
    // Force the query string to match FlatTemplateSetup's own canonical barcode pattern
    // (content/height/width) instead of whatever the legacy template specified - the
    // legacy content/height/width args are intentionally ignored here.
    const person = targetName || 'person'
    const regId = `${person}.${currentRegIdField}`
    const content = `(${regId} ? ${regId}.toString() : "")`
    return `("../Kiosk/${action}?content=" + ${content} + "&height=50&width=300") /* regId forced to ${currentRegIdField} to match FlatTemplateSetup - verify this is the right field for this template */`
}

let barcodeGuardCounter = 0

// Rewrites a literal "<img ... src='@Url.Action("RenderBarcode", "Print", new {...})' />" tag
// (anywhere in the raw body text, before normal parsing) into Razor-like text that produces
// FlatTemplateSetup's own guarded pattern:
//   const regId = person.X ? person.X.toString() : "";
//   if (regId.length > 4) { badgeContent += `<img ... src="...content=${regId}..." />`; }
// Only safe to apply where `targetName` resolves to a real in-scope variable (helper bodies
// with a single person-like param, and the main per-attendee loop body).
function wrapBarcodeImgWithGuard(body, targetName) {
    if (!targetName) return body
    const imgRe = /<img\b[^>]*?\bsrc\s*=\s*(['"])@Url\.Action\(\s*"(Render\w*(?:Barcode|QrCode|Code))"\s*,\s*"Print"\s*,\s*new\s*\{[\s\S]*?\}\s*\)\1[^>]*?\/?>/g
    return body.replace(imgRe, (fullMatch, quote, action) => {
        const n = barcodeGuardCounter++
        const regIdVar = `regId${n}`
        const field = `${targetName}.${currentRegIdField}`
        return `@{ var ${regIdVar} = ${field} ? ${field}.ToString() : ""; }\n` +
            `if (${regIdVar}.Length > 4)\n{\n` +
            `    <img alt="barcode" src="../Kiosk/${action}?content=@${regIdVar}&height=50&width=300" />\n}\n`
    })
}

// LINQ query-comprehension syntax ("from x in y join ... select ...") has no mechanical JS
// equivalent worth guessing at - swap the whole statement for a safe, clearly-flagged stub
// instead of emitting C# that would otherwise throw a SyntaxError and break the whole script.
function convertLinqQueryStatement(text) {
    if (!/\bfrom\s+\w+\s+in\s+/.test(text)) return null
    const stub = "[] /* TODO(convert): LINQ query syntax (\"from ... in ... select ...\") was not auto-converted - rewrite using .filter()/.map()/.find() */"
    const lhsMatch = /^[ \t]*(?:var|let|const)?\s*[\w.<>[\],]*\s+(\w+)\s*=/.exec(text)
    return lhsMatch ? `let ${lhsMatch[1]} = ${stub};` : `/* TODO(convert): LINQ query syntax ("from ... in ... select ...") was not auto-converted */`
}

function convertCSharpExpressionTokens(text, sourceParamName, targetName) {
    let js = text

    const linqStub = convertLinqQueryStatement(js)
    if (linqStub) return linqStub

    const barcode = convertUrlActionBarcode(js, sourceParamName, targetName)
    if (barcode) return barcode

    // Redundant "@" transitions (e.g. "@Model.X" written inside an already-code context)
    js = js.replace(/@(?=[A-Za-z_(])/g, '')

    // C# collection/array-initializer syntax -> JS array literal (parens before "{ }" are optional)
    js = js.replace(/new\s+(?:List|HashSet)<[^>]*>\s*(?:\(\s*\))?\s*\{([\s\S]*?)\}/g, (_, inner) => `[${inner}]`)
    js = js.replace(/new\s+(?:List|HashSet)<[^>]*>\s*(?:\(\s*\))?/g, '[]')
    js = js.replace(/new\s+\w+(?:<[^<>\n]*>)?\s*\[\]\s*\{([\s\S]*?)\}/g, (_, inner) => `[${inner}]`)
    js = js.replace(/new\s+\w+(?:<[^<>\n]*>)?\s*\[\]/g, '[]')
    // Any other "new SomeType()" (service/repository objects, DTOs, ...) has no JS equivalent -
    // stub it out as an empty object so at least the declaration itself doesn't throw.
    js = js.replace(/new\s+(\w+)\s*\(\s*\)/g, (_, type) => `{} /* TODO(convert): "new ${type}()" has no JS equivalent - likely server-side data access, wire up real data */`)

    // Must run before the generic ".Count"/".Count()" rules below, which would otherwise
    // eat the "Count" token first and leave "Model.AttendeeSelections.length" behind.
    js = js.replace(/\bModel\.AttendeeSelections\.Count\(\)/g, 'totalCount')
    js = js.replace(/\bModel\.AttendeeSelections\.Count\b/g, 'totalCount')

    js = js.replace(/(!?)[sS]tring\.IsNullOrEmpty\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g, (_, bang, arg) => (bang ? `(${arg.trim()})` : `!(${arg.trim()})`))
    js = js.replace(/\.ToUpper\(\)/g, '.toUpperCase()')
    js = js.replace(/\.ToLower\(\)/g, '.toLowerCase()')
    js = js.replace(/\.ToString\(\)/g, '.toString()')
    js = js.replace(/\.Trim\(\)/g, '.trim()')
    js = js.replace(/\.Contains\(/g, '.includes(')
    js = js.replace(/\.PadLeft\(([^,]+),\s*'([^']*)'\)/g, ".padStart($1, '$2')")
    js = js.replace(/\.Replace\(/g, '.replaceAll(')
    js = js.replace(/\.ToList(?:<[^<>]*>)?\(\)/g, '')
    js = js.replace(/\.ToArray(?:<[^<>]*>)?\(\)/g, '')
    js = js.replace(/\.ToHtmlString\(\)/g, '')
    js = js.replace(/\bHttpUtility\.HtmlDecode\(/g, 'htmlDecode(')
    js = js.replace(/\bHttpUtility\.HtmlEncode\(/g, 'htmlEncode(')
    js = js.replace(/\b(?:Int32|Int64|Convert)\.ToInt(?:32|64)?\(/g, 'parseInt(')
    js = js.replace(/\bInt32\.Parse\(/g, 'parseInt(')
    js = js.replace(/\bConvert\.ToString\(/g, 'String(')
    js = js.replace(/\bConvert\.ToBoolean\(/g, 'Boolean(')
    js = js.replace(/\bConvert\.ToDouble\(/g, 'parseFloat(')
    js = js.replace(/\.Length\b/g, '.length')
    // C# named arguments ("Foo(itemsPerPage: 3, count: x)") -> positional JS call args
    js = js.replace(/([(,]\s*)[A-Za-z_]\w*\s*:\s*(?!:)/g, '$1')
    // Where(pred).Any() / Where(pred).FirstOrDefault() - one level of nested parens in pred is tolerated
    js = js.replace(/\.Where\(([^()]*(?:\([^()]*\)[^()]*)*)\)\.Any\(\)/g, '.some($1)')
    js = js.replace(/\.Where\(([^()]*(?:\([^()]*\)[^()]*)*)\)\.FirstOrDefault\(\)/g, '.find($1)')
    js = js.replace(/\.Any\(\)/g, '.length > 0')
    js = js.replace(/\.(?:FirstOrDefault|First|SingleOrDefault|Single)\(\)/g, '[0] /* TODO(convert): verify empty-collection fallback - JS arrays return undefined, not null */')
    js = js.replace(/\.Last\(\)/g, '.slice(-1)[0]')
    js = js.replace(/\.ElementAt\(([^()]*)\)/g, '[$1]')
    js = js.replace(/\.Where\(/g, '.filter(')
    js = js.replace(/\.Count\(\)/g, '.length')
    js = js.replace(/\.Count\b/g, '.length')
    // .find()/[0] return undefined (not null) when missing - loosen strict null checks
    js = js.replace(/!==\s*null/g, '!= null')
    js = js.replace(/===\s*null/g, '== null')
    js = js.replace(/\bvar\s+/g, 'let ')
    // Explicit C# type declarations ("string x = ...;", "List<T> x = ...;") -> "let x = ...;"
    // Requires whitespace on at least one side of "=" so an HTML attribute like src="x" (no
    // spaces at all around "=") is never mistaken for a declaration.
    js = js.replace(/^(\s*)[A-Za-z_]\w*(?:<[^<>\n]*>)?(?:\[\])?\s+([A-Za-z_]\w*)(?:\s+=|=\s+)(?!=)/, '$1let $2 =')
    // C# implicit array/object initializer written as a bare "{ ... }" (no "new" keyword) RHS
    js = js.replace(/^(\s*let\s+\w+\s*=\s*)\{([^{}]*)\}(\s*;?\s*)$/, (full, pre, inner, post) => {
        if (/[A-Za-z_]\w*\s*=(?!=)/.test(inner)) {
            return `${pre}{${inner.replace(/([A-Za-z_]\w*)\s*=(?!=)/g, '$1:')}}${post}`
        }
        return `${pre}[${inner}]${post}`
    })

    if (sourceParamName && targetName) {
        const esc = escapeRegExp(sourceParamName)
        js = js.replace(new RegExp(`\\b${esc}\\.Attendee\\.`, 'g'), `${targetName}.`)
        js = js.replace(new RegExp(`\\b${esc}\\.`, 'g'), `${targetName}.`)
        js = js.replace(new RegExp(`\\b${esc}\\b(?!\\.)`, 'g'), targetName)
    }

    js = js.replace(/\bModel\.GetCountryByAbbreviation\(/g, 'getCountryByAbbreviation(')
    // Calls first (word-boundary after the captured name stops greedy backtracking from
    // landing mid-identifier, e.g. "GetSessions(" must not be read as "GetSession" + "s(")
    js = js.replace(/\bModel\.(\w+)\b\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g, (_, prop) => `[] /* TODO(convert): Model.${prop}(...) has no JS equivalent - wire up real data */`)
    js = js.replace(/\bModel\.(\w+)\b(?!\()/g, (_, prop) => `false /* TODO(convert): Model.${prop} has no JS equivalent - wire up real data/flag */`)

    return js
}

function buildTemplateLiteral(segments) {
    let out = '`'
    for (const seg of segments) {
        if (seg.type === 'text') {
            out += seg.value.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${')
        } else {
            out += '${' + seg.value + '}'
        }
    }
    out += '`'
    return out
}

function parseInlineExpr(body, i, sourceParamName, targetName) {
    let j = i + 1
    if (body[j] === '(') {
        const end = matchBalanced(body, j, '(', ')')
        return { value: convertCSharpExpressionTokens(body.slice(j + 1, end), sourceParamName, targetName), nextIndex: end + 1 }
    }
    const identMatch = /^[A-Za-z_]\w*/.exec(body.slice(j))
    if (!identMatch) return { value: '', nextIndex: j }
    j += identMatch[0].length
    for (;;) {
        if (body[j] === '.' && /[A-Za-z_]/.test(body[j + 1] || '')) {
            j += 1
            const next = /^[A-Za-z_]\w*/.exec(body.slice(j))
            j += next[0].length
            continue
        }
        if (body[j] === '(') { j = matchBalanced(body, j, '(', ')') + 1; continue }
        if (body[j] === '[') { j = matchBalanced(body, j, '[', ']') + 1; continue }
        break
    }
    return { value: convertCSharpExpressionTokens(body.slice(i + 1, j), sourceParamName, targetName), nextIndex: j }
}

function branchToExpr(result) {
    const literal = buildTemplateLiteral(result.segments)
    if (result.statements.length === 0) return literal
    return `(() => {\n${result.statements.join('\n')}\nreturn ${literal};\n})()`
}

function tryMatchBareStatement(body, i, sourceParamName, targetName) {
    const rest = body.slice(i)
    const commentMatch = /^[ \t]*\/\/[^\n]*/.exec(rest)
    if (commentMatch) return { js: '', nextIndex: i + commentMatch[0].length }
    // Requires whitespace on at least one side of "=" - excludes HTML attributes like
    // src="x" or class='y' (no spaces around "=" at all), which otherwise look identical
    // to a bare C# assignment statement.
    const startMatch = /^[ \t]*(?:(?:var|let|const)\s+\w+\s*=|[A-Za-z_]\w*(?:<[^<>\n]*>)?(?:\[\])?\s+[A-Za-z_]\w*(?:\s+=\s*|=\s+)[^=\n]|[A-Za-z_][\w.]*(?:\s+=\s*|=\s+)[^='"])/.exec(rest)
    if (!startMatch) return null
    let depth = 0
    let j = i
    for (; j < body.length; j++) {
        const ch = body[j]
        if (ch === '(' || ch === '{' || ch === '[') depth++
        else if (ch === ')' || ch === '}' || ch === ']') { depth--; if (depth < 0) return null }
        else if (ch === ';' && depth === 0) { j++; break }
    }
    if (j >= body.length) return null
    const raw = body.slice(i, j)
    if (!/;\s*$/.test(raw)) return null
    return { js: convertCSharpExpressionTokens(raw, sourceParamName, targetName), nextIndex: j }
}

// Guard-clause idiom: "if (cond) { return; }" / "if (cond) { return expr; }" with no else.
// Needs real statement-level control flow (to actually short-circuit the enclosing
// function) rather than the ternary/IIFE expression used for markup-producing ifs.
function tryMatchGuardReturn(body, i, sourceParamName, targetName) {
    if (!/^\s*@?if\s*\(/.test(body.slice(i))) return null
    const parenIdx = body.indexOf('(', i)
    const parenEnd = matchBalanced(body, parenIdx, '(', ')')
    const braceIdx = body.indexOf('{', parenEnd)
    if (braceIdx === -1 || !/^\s*$/.test(body.slice(parenEnd + 1, braceIdx))) return null
    const braceEnd = matchBalanced(body, braceIdx, '{', '}')
    const innerBody = body.slice(braceIdx + 1, braceEnd)
    const returnMatch = /^\s*return\s*([^;]*);\s*$/.exec(innerBody)
    if (!returnMatch) return null
    const after = braceEnd + 1
    const wsLen = /^\s*/.exec(body.slice(after))[0].length
    if (/^@?else\b/.test(body.slice(after + wsLen))) return null
    const cond = convertCSharpExpressionTokens(body.slice(parenIdx + 1, parenEnd), sourceParamName, targetName)
    const retExpr = returnMatch[1].trim()
    const jsReturn = retExpr ? convertCSharpExpressionTokens(retExpr, sourceParamName, targetName) : "''"
    return { js: `if (${cond}) { return ${jsReturn}; }`, nextIndex: after }
}

// A bare "return;" / "return expr;" not wrapped in an if - common at the end of @functions
// methods (which are pure procedural C#, not markup-emitting @helper bodies).
function tryMatchReturnStatement(body, i, sourceParamName, targetName) {
    const m = /^[ \t]*return\s*([^;\n]*);/.exec(body.slice(i))
    if (!m) return null
    const exprRaw = m[1].trim()
    const js = exprRaw ? `return ${convertCSharpExpressionTokens(exprRaw, sourceParamName, targetName)};` : 'return;'
    return { js, nextIndex: i + m[0].length }
}

let fragCounter = 0

// A "foreach" appearing inside a helper/function body (not the main attendee loop, which is
// handled separately). Runs as a real statement; any markup it produces per-iteration is
// accumulated into a uniquely-named fragment variable and spliced back in as a segment.
function tryMatchForeach(body, i, sourceParamName, targetName) {
    const header = /^\s*foreach\s*\(/.exec(body.slice(i))
    if (!header) return null
    const parenIdx = i + header[0].length - 1
    const parenEnd = matchBalanced(body, parenIdx, '(', ')')
    const headerText = body.slice(parenIdx + 1, parenEnd)
    const declMatch = /^\s*\w+\s+(\w+)\s+in\s+([\s\S]+)$/.exec(headerText)
    if (!declMatch) return null
    const loopVar = declMatch[1]
    const collectionExpr = convertCSharpExpressionTokens(declMatch[2].trim(), sourceParamName, targetName)
    const braceIdx = body.indexOf('{', parenEnd)
    if (braceIdx === -1) return null
    const braceEnd = matchBalanced(body, braceIdx, '{', '}')
    const innerBody = body.slice(braceIdx + 1, braceEnd)
    const result = parseMixedBody(innerBody, sourceParamName, targetName)
    const bodyStatements = [...result.statements]
    let fragVar = null
    if (result.segments.length > 0) {
        fragVar = `__frag${fragCounter++}`
        bodyStatements.push(`${fragVar} += ${buildTemplateLiteral(result.segments)};`)
    }
    const loopJs = `(${collectionExpr} || []).forEach(${loopVar} => {\n${indent(bodyStatements.join('\n'), 4)}\n});`
    const js = fragVar ? `let ${fragVar} = '';\n${loopJs}` : loopJs
    return { js, nextIndex: braceEnd + 1, fragVar }
}

function parseIfChain(body, startIdx, sourceParamName, targetName) {
    let i = startIdx
    const branches = []
    for (;;) {
        const rest = body.slice(i)
        const elseIfMatch = /^\s*@?else\s+if\s*\(/.exec(rest)
        const plainElseMatch = /^\s*@?else\b/.exec(rest)
        let cond = null
        let afterKeyword
        if (elseIfMatch) {
            const parenIdx = i + rest.indexOf('(')
            const parenEnd = matchBalanced(body, parenIdx, '(', ')')
            cond = convertCSharpExpressionTokens(body.slice(parenIdx + 1, parenEnd), sourceParamName, targetName)
            afterKeyword = parenEnd + 1
        } else if (plainElseMatch) {
            afterKeyword = i + plainElseMatch[0].length
        } else {
            const parenIdx = body.indexOf('(', i)
            const parenEnd = matchBalanced(body, parenIdx, '(', ')')
            cond = convertCSharpExpressionTokens(body.slice(parenIdx + 1, parenEnd), sourceParamName, targetName)
            afterKeyword = parenEnd + 1
        }
        const braceIdx = body.indexOf('{', afterKeyword)
        if (braceIdx === -1) throw new Error('unbalanced or brace-less if/else block')
        const braceEnd = matchBalanced(body, braceIdx, '{', '}')
        const innerBody = body.slice(braceIdx + 1, braceEnd)
        const result = parseMixedBody(innerBody, sourceParamName, targetName)
        branches.push({ cond, result })
        i = braceEnd + 1
        const wsLen = /^\s*/.exec(body.slice(i))[0].length
        if (/^@?else\b/.test(body.slice(i + wsLen))) { i += wsLen; continue }
        break
    }
    let expr = "''"
    for (let b = branches.length - 1; b >= 0; b--) {
        const frag = branchToExpr(branches[b].result)
        expr = branches[b].cond === null ? frag : `(${branches[b].cond}) ? (${frag}) : (${expr})`
    }
    return { expr, nextIndex: i }
}

function parseMixedBody(body, sourceParamName, targetName) {
    const statements = []
    const segments = []
    let i = 0
    let guardIterations = 0
    const guardLimit = body.length * 4 + 100
    while (i < body.length) {
        if (++guardIterations > guardLimit) {
            throw new Error('parseMixedBody did not make progress - likely an unbalanced brace/paren in the source')
        }
        if (body.startsWith('@*', i)) {
            const end = body.indexOf('*@', i + 2)
            i = end === -1 ? body.length : end + 2
            continue
        }
        if (body.startsWith('@{', i)) {
            const braceIdx = i + 1
            const endIdx = matchBalanced(body, braceIdx, '{', '}')
            const nested = parseMixedBody(body.slice(braceIdx + 1, endIdx), sourceParamName, targetName)
            statements.push(...nested.statements)
            segments.push(...nested.segments)
            i = endIdx + 1
            continue
        }
        const foreachMatch = tryMatchForeach(body, i, sourceParamName, targetName)
        if (foreachMatch) {
            statements.push(foreachMatch.js)
            if (foreachMatch.fragVar) segments.push({ type: 'expr', value: foreachMatch.fragVar })
            i = foreachMatch.nextIndex
            continue
        }
        const guardReturn = tryMatchGuardReturn(body, i, sourceParamName, targetName)
        if (guardReturn) {
            statements.push(guardReturn.js)
            i = guardReturn.nextIndex
            continue
        }
        const returnStatement = tryMatchReturnStatement(body, i, sourceParamName, targetName)
        if (returnStatement) {
            statements.push(returnStatement.js)
            i = returnStatement.nextIndex
            continue
        }
        if (/^\s*@?if\s*\(/.test(body.slice(i))) {
            const { expr, nextIndex } = parseIfChain(body, i, sourceParamName, targetName)
            segments.push({ type: 'expr', value: expr })
            i = nextIndex
            continue
        }
        // Razor auto-detects code-mode for bare C# statements even inside a markup-default
        // @helper body - look for them before falling back to literal markup text.
        const bareStatement = tryMatchBareStatement(body, i, sourceParamName, targetName)
        if (bareStatement) {
            if (bareStatement.js) statements.push(bareStatement.js)
            i = bareStatement.nextIndex
            continue
        }
        if (body.startsWith('@:', i)) { i += 2; continue }
        if (body[i] === '@' && /[\w(]/.test(body[i + 1] || '')) {
            const { value, nextIndex } = parseInlineExpr(body, i, sourceParamName, targetName)
            segments.push({ type: 'expr', value })
            // Razor allows "@HelperCall(args);" as a statement-style call - drop the stray ";"
            i = body[nextIndex] === ';' ? nextIndex + 1 : nextIndex
            continue
        }
        // Plain markup/text: advance one line at a time so bare statements/if-chains that
        // start partway through a run of text are still caught at the top of the next line.
        const atIdx = body.indexOf('@', i)
        const nlIdx = body.indexOf('\n', i)
        let end
        if (atIdx !== -1 && (nlIdx === -1 || atIdx <= nlIdx)) end = atIdx
        else if (nlIdx !== -1) end = nlIdx + 1
        else end = body.length
        if (end === i) end = i + 1
        segments.push({ type: 'text', value: body.slice(i, end) })
        i = end
    }
    return { statements, segments }
}

function safely(label, warnings, fn) {
    try {
        return fn()
    } catch (err) {
        warnings.push(`${label}: conversion failed (${err.message}) - left as a stub, finish it by hand.`)
        return null
    }
}

function convertHelperToJs(helper, warnings) {
    const singleParam = helper.params.length === 1
    const sourceParamName = singleParam ? helper.params[0].name : null
    const targetName = singleParam ? 'person' : null
    const jsParamNames = singleParam ? ['person'] : (helper.params.length ? helper.params.map(p => p.jsParam) : ['person'])
    return safely(`Helper "${helper.name}"`, warnings, () => {
        const guardedBody = wrapBarcodeImgWithGuard(helper.body, targetName)
        const { statements, segments } = parseMixedBody(guardedBody, sourceParamName, targetName)
        const literal = buildTemplateLiteral(segments)
        const bodyJs = [...statements, `return ${literal};`].join('\n')
        const js = `function ${helper.name}(${jsParamNames.join(', ')}) {\n${indent(bodyJs, 4)}\n}`
        if (/TODO\(convert\)/.test(js)) {
            warnings.push(`Helper "${helper.name}": contains TODO(convert) markers - review before using in production.`)
        }
        return js
    }) || `function ${helper.name}(${jsParamNames.join(', ')}) {\n    /* TODO(convert): automatic conversion failed - rewrite by hand */\n    return '';\n}`
}

function convertFunctionToJs(fn, warnings) {
    return safely(`Function "${fn.name}"`, warnings, () => {
        // @functions methods are pure procedural C# (no markup) - reuse the same statement/if/
        // foreach/return machinery as helpers, but only fall back to a trailing template-literal
        // return if nothing already returned explicitly (the normal case for these methods).
        const { statements, segments } = parseMixedBody(fn.body, fn.paramName, fn.paramName)
        const bodyJs = segments.length ? [...statements, `return ${buildTemplateLiteral(segments)};`] : statements
        const js = `function ${fn.name}(${fn.paramName}) {\n${indent(bodyJs.join('\n'), 4)}\n}`
        if (/TODO\(convert\)/.test(js)) {
            warnings.push(`Function "${fn.name}": contains TODO(convert) markers - review before using in production.`)
        }
        return js
    }) || `function ${fn.name}(${fn.paramName}) {\n    /* TODO(convert): automatic conversion failed - rewrite by hand */\n    return null;\n}`
}

function convertForLoopToJs(forLoop, warnings) {
    return safely('Main per-attendee markup (@for loop)', warnings, () => {
        const { mainBody, guestBody, guestParamName } = extractAndStripGuestLoop(forLoop.body)
        const guardedMainBody = wrapBarcodeImgWithGuard(mainBody, 'person')
        const mainResult = parseMixedBody(guardedMainBody, forLoop.paramName, 'person')
        const mainLiteral = buildTemplateLiteral(mainResult.segments)
        const mainJs = `function generateAttendeeBlockHTML(person, ${forLoop.indexVar}, totalCount) {\n` +
            `${indent([...mainResult.statements, `return ${mainLiteral};`].join('\n'), 4)}\n}`
        if (/TODO\(convert\)/.test(mainJs)) {
            warnings.push('generateAttendeeBlockHTML: contains TODO(convert) markers - review before using in production.')
        }

        let guestJs = null
        if (guestBody) {
            const guestResult = safely('Guest loop body', warnings, () => parseMixedBody(guestBody, guestParamName, 'guest'))
            if (guestResult) {
                const guestLiteral = buildTemplateLiteral(guestResult.segments)
                guestJs = `function generateGuestBadgeHTML(guest) {\n` +
                    `${indent([...guestResult.statements, `return ${guestLiteral};`].join('\n'), 4)}\n}`
                if (/TODO\(convert\)/.test(guestJs)) {
                    warnings.push('generateGuestBadgeHTML: contains TODO(convert) markers - review before using in production.')
                }
            }
        }
        return { mainJs, guestJs, hasGuestLoop: !!guestBody }
    })
}

// Helper functions auto-injected into the output when their corresponding call is detected,
// so the generated script never references an undefined global.
const RUNTIME_STUBS = [
    {
        test: /\bgetCountryByAbbreviation\(/,
        js: "function getCountryByAbbreviation(code) {\n    /* TODO(convert): implement country-code -> country-name lookup (was Model.GetCountryByAbbreviation) */\n    return code || '';\n}",
    },
    {
        test: /\bhtmlDecode\(/,
        js: "function htmlDecode(str) {\n    const el = document.createElement('textarea');\n    el.innerHTML = str || '';\n    return el.value;\n}",
    },
    {
        test: /\bhtmlEncode\(/,
        js: "function htmlEncode(str) {\n    const el = document.createElement('div');\n    el.textContent = str || '';\n    return el.innerHTML;\n}",
    },
]

function buildRuntimeStubs(combinedText) {
    return RUNTIME_STUBS.filter(s => s.test.test(combinedText)).map(s => s.js).join('\n\n')
}

function buildFlatTemplateDocument({ css, preambleJs, functionsJs, helpersJs, mainJs, guestJs, hasGuestLoop, stubsJs }) {
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
${indent(css, 4)}
</head>
<body>
    <div id="badges-container"></div>
    <script>
        /* 1. DATA INJECTION - the backend replaces %ATTENDEEINFO% with a JSON array string. */
        const attendees = "%ATTENDEEINFO%";
${preambleJs ? `\n        /* Page-level declarations converted from top-level @{ } blocks. */\n${indent(preambleJs, 8)}\n` : ''}
        /* 2. Converted from @functions / @helper blocks - review any TODO(convert) markers below. */
${stubsJs ? `${indent(stubsJs, 8)}\n\n` : ''}${indent(functionsJs, 8)}

${indent(helpersJs, 8)}

        /* 3. Converted from the main @for loop body - one call per attendee. */
${indent(mainJs, 8)}
${guestJs ? `\n${indent(guestJs, 8)}\n` : ''}
        /* 4. INITIALIZATION LOOP */
        function renderAllBadges() {
            const container = document.getElementById("badges-container");
            let finalHTML = "";
            const parsedAttendees = JSON.parse(attendees);
            parsedAttendees.forEach((attendee, i) => {
                finalHTML += generateAttendeeBlockHTML(attendee, i, parsedAttendees.length);
${hasGuestLoop ? `                const guests = attendee.Guests || [];
                guests.forEach(guest => { finalHTML += generateGuestBadgeHTML(guest); });
` : ''}            });
            container.innerHTML = finalHTML;
        }

        /* Run the script as soon as the window loads */
        window.onload = renderAllBadges;
    </script>
</body>
</html>`
}

export function convertCshtmlToFlatTemplate(rawSource, modelFamily = 'other') {
    const warnings = []
    if (!rawSource || !rawSource.trim()) {
        return { templateString: '', warnings: ['Paste .cshtml source above, then click Convert.'], helperNames: [] }
    }
    currentRegIdField = REGID_FIELD_BY_FAMILY[modelFamily] || 'LocalRegistrationId'
    try {
        const source = stripRazorComments(rawSource)

        const css = extractStyles(source)
        if (!css) warnings.push('No <style> block found inside @section styles - badge CSS was left empty.')
        if (/src=["']~\//.test(source)) {
            warnings.push('Found ASP.NET root-relative asset paths ("~/...") - update them to real URLs your React app can serve.')
        }

        const helperDefs = extractHelpers(source)
        const functionDefs = extractFunctionsBlock(source)
        const functionsJs = functionDefs.map(fn => convertFunctionToJs(fn, warnings)).join('\n\n')
        const helpersJs = helperDefs.map(h => convertHelperToJs(h, warnings)).join('\n\n')

        const preambleBoundaryCandidates = [source.indexOf('@for'), source.search(/@helper\s+\w+\s*\(/), source.indexOf('@functions')]
            .filter(idx => idx !== -1)
        const preambleBoundary = preambleBoundaryCandidates.length ? Math.min(...preambleBoundaryCandidates) : source.length
        const preambleEntries = safely('Page-level @{ } declarations', warnings, () => extractPagePreambleEntries(source, preambleBoundary)) || []

        const forLoop = extractForLoop(source)
        let mainJs, guestJs, hasGuestLoop

        if (forLoop) {
            const result = convertForLoopToJs(forLoop, warnings)
            if (result) {
                ;({ mainJs, guestJs, hasGuestLoop } = result)
            }
        }
        if (!mainJs) {
            if (helperDefs.length === 0) {
                warnings.push('No @for loop or @helper blocks were found - nothing to convert into badge-rendering functions.')
            } else {
                warnings.push('No @for loop body could be converted - falling back to calling the largest @helper directly; wire up generateAttendeeBlockHTML manually if this guess is wrong.')
            }
            const mainHelper = helperDefs.find(h => !/guest|upsidedown|blank|barcode/i.test(h.name)) || helperDefs[0]
            const guestHelper = helperDefs.find(h => /guest/i.test(h.name))
            mainJs = `function generateAttendeeBlockHTML(person, i, totalCount) {\n` +
                `    return ${mainHelper ? `${mainHelper.name}(person)` : "''"};\n}`
            guestJs = guestHelper ? `function generateGuestBadgeHTML(guest) {\n    return ${guestHelper.name}(guest);\n}` : null
            hasGuestLoop = !!guestHelper
        }

        // Only keep preamble declarations that are actually referenced later (repository/service
        // aliases etc.) - drop unused ones (e.g. "var attendeeId = selections.First()...;" debug
        // leftovers) so we don't execute - and potentially crash on - dead C# LINQ expressions.
        const restText = functionsJs + helpersJs + mainJs + (guestJs || '')
        const keepPreamble = new Set()
        preambleEntries.forEach((e, idx) => {
            if (!e.name || new RegExp(`\\b${escapeRegExp(e.name)}\\b`).test(restText)) keepPreamble.add(idx)
        })
        // Transitive closure: a kept line may itself reference an earlier preamble declaration
        // (e.g. "customFields = udfr.GetUdfs(..., attendeeId)" depends on "attendeeId" above it).
        for (let changed = true; changed; ) {
            changed = false
            preambleEntries.forEach((entry, idx) => {
                if (keepPreamble.has(idx) || !entry.name) return
                const referencedByKept = preambleEntries.some((other, otherIdx) =>
                    keepPreamble.has(otherIdx) && otherIdx !== idx && new RegExp(`\\b${escapeRegExp(entry.name)}\\b`).test(other.js))
                if (referencedByKept) { keepPreamble.add(idx); changed = true }
            })
        }
        const preambleJs = preambleEntries.filter((e, idx) => keepPreamble.has(idx)).map(e => e.js).join('\n')

        const stubsJs = buildRuntimeStubs(preambleJs + functionsJs + helpersJs + mainJs + (guestJs || ''))

        const templateString = buildFlatTemplateDocument({
            css,
            preambleJs,
            functionsJs,
            helpersJs,
            mainJs,
            guestJs,
            hasGuestLoop,
            stubsJs,
        })

        if (/\bDateTime\./.test(templateString)) {
            warnings.push('Found C# DateTime usage - there is no JS equivalent conversion rule yet; rewrite those checks using Date manually.')
        }
        if (/Render\w*\?content=/.test(templateString)) {
            warnings.push(`Barcode <img> src was forced to match FlatTemplateSetup's own pattern (content/height=50/width=300), reading "${currentRegIdField}" as regId - change the model type dropdown if this isn't the right field.`)
        }

        warnings.push('Review every TODO(convert) marker in the output below before saving it in Flat Template Setup.')

        return { templateString, warnings, helperNames: helperDefs.map(h => h.name) }
    } catch (err) {
        warnings.push(`Conversion failed: ${err.message}. Fix the pasted source (unbalanced braces/parens are the most common cause) and retry.`)
        return { templateString: '', warnings, helperNames: [] }
    }
}

// Best-effort guess at which legacy model family a pasted .cshtml belongs to, read off the
// "@model ...XyzDisplayVm" line. Purely cosmetic/labeling + picks a matching test-data
// preset - the converter engine above is generic and does not branch on this value.
export function detectModelFamily(source) {
    // "Badges.Web.Models.Badge.XyzDisplayVm" - Xyz is empty for the plain/generic VM.
    const m = /@model\s+[\w.]*\.(\w*?)DisplayVm/.exec(source || '')
    if (!m) return 'other'
    if (!m[1]) return 'generic'
    const known = ['Swoogo', 'Impexium', 'Rhythm', 'Er', 'Etouch', 'Ms', 'iMIS']
    const hit = known.find(name => name.toLowerCase() === m[1].toLowerCase())
    return hit ? hit.toLowerCase() : 'other'
}

export const MODEL_FAMILY_OPTIONS = [
    { value: 'auto', label: 'Auto-detect from @model line' },
    { value: 'er', label: 'ER' },
    { value: 'generic', label: 'Generic (DisplayVm)' },
    { value: 'swoogo', label: 'Swoogo' },
    { value: 'etouch', label: 'eTouch' },
    { value: 'impexium', label: 'Impexium' },
    { value: 'ms', label: 'MemberSuite (Ms)' },
    { value: 'rhythm', label: 'Rhythm' },
    { value: 'imis', label: 'iMIS' },
    { value: 'other', label: 'Other (Cvent, ePly, GTR, ...)' },
]

// Real shape actually produced by the live system for %ATTENDEEINFO% (captured from
// production), shared as a common base across every model family below and then layered
// with each family's own business fields. Kept verbatim, including the pre-computed
// *Smaller/*Smallest/*_Combo fields, the barcode ScannerField image, and the Guests shape.
const BASE_PERSON_SAMPLE = {
    LocalRegistrationId: 24062,
    ScannerField: 'data:image/jpeg;base64,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',
    FirstName: 'Sebastien',
    LastName: 'Heintzelman',
    Organization: 'National Association of Wholesaler-Distributors (NAW)',
    Title: 'General Manager',
    EmailAddress: 'sebastien.morasse@valonkone.com',
    FirstNameSmaller: 'Sebastien',
    FirstNameSmallest: 'Sebastien',
    LastNameSmaller: 'Heintzelman',
    LastNameSmallest: 'Heintzelman',
    LastNameFirstName_Combo: 'Morasse, Sebastien',
    OrganizationSmaller: 'National Association of Wholesaler-Distributors (NAW)',
    OrganizationSmallest: 'National Association of Wholesaler-Distributors (NAW)',
    FullName_Combo: 'Sebastien Morasse',
    FullNameTitle_Combo: 'Sebastien Morasse, General Manager',
    Guests: [
        {
            LocalRegistrationId: 24062,
            FirstName: 'Sebastien',
            LastName: 'Heintzelman',
            Organization: 'National Association of Wholesaler-Distributors (NAW)',
            FirstNameSmaller: 'Sebastien',
            FirstNameSmallest: 'Sebastien',
            LastNameSmaller: 'Heintzelman',
            LastNameSmallest: 'Heintzelman',
            LastNameFirstName_Combo: 'Morasse, Sebastien',
            OrganizationSmaller: 'National Association of Wholesaler-Distributors (NAW)',
            OrganizationSmallest: 'National Association of Wholesaler-Distributors (NAW)',
            FullName_Combo: 'Sebastien Morasse',
        },
    ],
}

// Per-family business fields layered on top of BASE_PERSON_SAMPLE above.
const SAMPLE_TEST_DATA = {
    swoogo: [{
        ...BASE_PERSON_SAMPLE,
        City: 'Chicago',
        State: 'IL',
        Country: 'US',
        RegistrationId: 10234,
        RegType: 'Attendee - Member',
        LineItems: [],
        EventQuestions: [],
        CEntries: [],
    }],
    impexium: [{
        ...BASE_PERSON_SAMPLE,
        PrimaryOrganization: BASE_PERSON_SAMPLE.Organization,
        BadgeOrganization: BASE_PERSON_SAMPLE.Organization,
        City: 'Austin',
        State: 'TX',
        Country: 'US',
        LocalClientId: 55231,
        RegistrantTypeName: 'Attendee',
        CustomFields: [],
        Sessions: [],
    }],
    rhythm: [{
        ...BASE_PERSON_SAMPLE,
        City: 'Denver',
        State: 'CO',
        Country: 'US',
        Number: 78341,
        PackageId: 'full-conference',
        TypeId: 'attendee',
        CustomFields: [],
        Functions: [],
    }],
    er: [{
        ...BASE_PERSON_SAMPLE,
        TableNumber: 12,
        City: 'Atlanta',
        State: 'GA',
        Country: 'US',
        RegistrationId: 44210,
        RegType: 'Attendee',
        LineItems: [],
        EventQuestions: [],
    }],
    etouch: [{
        ...BASE_PERSON_SAMPLE,
        Suffix: 'MD',
        Region: 'Northeast',
        Ribbons: [],
        City: 'Boston',
        State: 'MA',
        Country: 'US',
        RegistrationId: 33102,
    }],
    ms: [{
        ...BASE_PERSON_SAMPLE,
        LocalID: 'M-10045',
        VendorCode: '',
        City: 'Denver',
        State: 'CO',
        Country: 'US',
    }],
    generic: [{
        ...BASE_PERSON_SAMPLE,
        VendorCode: '',
        City: 'Chicago',
        State: 'IL',
        Country: 'US',
        RegistrationId: 10234,
    }],
    imis: [{
        ...BASE_PERSON_SAMPLE,
        PartyId: 60214,
        RegistrationType: 'Attendee',
        CityName: 'Columbus',
        StateName: 'OH',
        CountryName: 'US',
    }],
    other: [{
        ...BASE_PERSON_SAMPLE,
        Company: BASE_PERSON_SAMPLE.Organization,
        City: 'Chicago',
        State: 'IL',
        Country: 'US',
        CityState_Combo: 'Chicago, IL',
        RegistrationId: 10234,
        RegType: 'Attendee - Member',
        LineItems: [],
        EventQuestions: [],
        CEntries: [],
    }],
}

export function getSampleTestData(modelFamily = 'other') {
    const rows = SAMPLE_TEST_DATA[modelFamily] || SAMPLE_TEST_DATA.other
    return JSON.stringify(rows, null, 2)
}
